// Microsoft Entra ID sign-in for Lens v2.0.
//
// This module used to run OAuth PKCE through Tauri's Rust host with a loopback
// listener; that flow is retired. v2.0 runs entirely in the browser using
// @azure/msal-browser, popup flow. The public surface (AuthUser, AuthSession,
// interactiveSignIn, silentSignIn, signOut) is preserved so existing callers
// (App.tsx, LoginScreen, store) keep working.
//
// New: `acquireLensApiToken()` — used by src/lib/api/* to fetch a bearer
// token scoped to the Lens Function App. Callers must be signed in.

import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from "@azure/msal-browser";

// Public identifiers, not secrets. Registered 2026-08 in Adapt's tenant.
export const AUTH_CONFIG = {
  tenantId: "b4a90595-2989-4706-b922-ce2ce310e21a",
  clientId: "6fce4aec-c48c-44ae-8be5-f062b46d8ce8",
  lensApiScope: "api://6fce4aec-c48c-44ae-8be5-f062b46d8ce8/access_as_user",
  graphScopes: ["User.Read", "User.ReadBasic.All"],
  loginScopes: ["openid", "profile", "email"],
} as const;

const AUTHORITY = `https://login.microsoftonline.com/${AUTH_CONFIG.tenantId}`;

export type Role = "admin" | "recruiter";

export interface AuthUser {
  oid: string;
  displayName: string;
  email: string;
  role: Role | null;
}

export interface AuthSession {
  user: AuthUser;
  accessToken: string; // Microsoft Graph token — used for /me and photos
  expiresAt: number;
}

// ---------------------------------------------------------------------------
// MSAL singleton — one PublicClientApplication for the whole app.

let msalInstance: PublicClientApplication | null = null;
let msalInitPromise: Promise<PublicClientApplication> | null = null;

async function getMsal(): Promise<PublicClientApplication> {
  if (msalInstance) return msalInstance;
  if (msalInitPromise) return msalInitPromise;
  msalInitPromise = (async () => {
    const instance = new PublicClientApplication({
      auth: {
        clientId: AUTH_CONFIG.clientId,
        authority: AUTHORITY,
        // Root of the SPA — must be registered as an SPA redirect URI
        // in the Entra app. Both localhost:5173 and the SWA host are.
        redirectUri: window.location.origin + "/",
      },
      cache: { cacheLocation: "sessionStorage" },
    });
    await instance.initialize();

    // Consume any auth response sitting in the URL fragment (from a redirect
    // login — either an intentional loginRedirect or a popup that the browser
    // blocked and MSAL fell back to redirect for). Without this, the response
    // stays in the fragment unprocessed and later sign-in calls fail.
    try {
      const result = await instance.handleRedirectPromise();
      if (result?.account) {
        instance.setActiveAccount(result.account);
      }
    } catch (err) {
      console.warn("MSAL handleRedirectPromise failed", err);
    }

    msalInstance = instance;
    return instance;
  })();
  return msalInitPromise;
}

// ---------------------------------------------------------------------------

function parseUser(account: AccountInfo): AuthUser {
  const claims = (account.idTokenClaims ?? {}) as Record<string, unknown>;
  const roles = Array.isArray(claims.roles) ? (claims.roles as string[]) : [];
  const role: Role | null = roles.includes("admin")
    ? "admin"
    : roles.includes("recruiter")
      ? "recruiter"
      : null;
  return {
    oid: (claims.oid as string) ?? account.localAccountId,
    displayName: account.name ?? account.username ?? "Unknown",
    email: account.username ?? "",
    role,
  };
}

async function acquireGraphToken(account: AccountInfo): Promise<{ token: string; expiresOn: Date | null }> {
  const m = await getMsal();
  try {
    const result = await m.acquireTokenSilent({
      scopes: [...AUTH_CONFIG.graphScopes],
      account,
    });
    return { token: result.accessToken, expiresOn: result.expiresOn };
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const result = await m.acquireTokenPopup({
        scopes: [...AUTH_CONFIG.graphScopes],
        account,
      });
      return { token: result.accessToken, expiresOn: result.expiresOn };
    }
    throw err;
  }
}

function toSession(user: AuthUser, graphToken: string, expiresOn: Date | null): AuthSession {
  return {
    user,
    accessToken: graphToken,
    expiresAt: expiresOn ? expiresOn.getTime() : Date.now() + 3600_000,
  };
}

/**
 * Interactive sign-in via full-page redirect. Chosen over popup because
 * popup behavior varies across browsers (Firefox/Zen block or double-open
 * on user-triggered popups). Redirect is the boring, reliable path.
 *
 * The page navigates to Microsoft, the user authenticates, and Entra
 * redirects back to redirectUri with the auth response in the URL
 * fragment. On that next load, `handleRedirectPromise()` in getMsal init
 * consumes the fragment, sets the active account, and App.tsx's silent
 * sign-in effect picks it up naturally.
 *
 * Return type is Promise<AuthSession> for interface compatibility, but
 * this promise never resolves — the page navigates before it would.
 * Callers should treat the call as fire-and-forget.
 */
export async function interactiveSignIn(): Promise<AuthSession> {
  const m = await getMsal();
  const scopes = [...AUTH_CONFIG.loginScopes, AUTH_CONFIG.lensApiScope];
  // Fire and forget — navigation happens inside loginRedirect.
  void m.loginRedirect({ scopes });
  // Stall so the caller doesn't see a resolution / error during navigation.
  return new Promise<AuthSession>(() => {});
}

/** Silent sign-in from MSAL cache. Null = interactive needed. */
export async function silentSignIn(): Promise<AuthSession | null> {
  const m = await getMsal();
  const accounts = m.getAllAccounts();
  if (accounts.length === 0) return null;
  const account = accounts[0]!;
  m.setActiveAccount(account);
  try {
    const user = parseUser(account);
    const { token, expiresOn } = await acquireGraphToken(account);
    return toSession(user, token, expiresOn);
  } catch (err) {
    console.warn("Silent sign-in failed", err);
    return null;
  }
}

/** Sign out via redirect — same reasoning as interactiveSignIn. */
export async function signOut(): Promise<void> {
  const m = await getMsal();
  const account = m.getActiveAccount();
  if (!account) return;
  void m.logoutRedirect({ account });
}

/**
 * Acquire a Lens API access token for the current user.
 * Used by src/lib/api/* to authorize calls to func-lens-core.
 *
 * Silently refreshes when possible; falls back to popup interaction only
 * when the refresh token has expired.
 */
export async function acquireLensApiToken(): Promise<string> {
  const m = await getMsal();
  const account = m.getActiveAccount() ?? m.getAllAccounts()[0];
  if (!account) throw new Error("Not signed in");
  try {
    const result = await m.acquireTokenSilent({
      scopes: [AUTH_CONFIG.lensApiScope],
      account,
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const result = await m.acquireTokenPopup({
        scopes: [AUTH_CONFIG.lensApiScope],
        account,
      });
      return result.accessToken;
    }
    throw err;
  }
}

/**
 * Acquire an access token for the Stack API.
 *
 * TEMPORARY: delegates to acquireLensApiToken(). Stack's backend currently
 * verifies JWT signature only (see stack-api/src/shared/auth.ts) — audience
 * is not checked, so any Entra-signed token for this tenant validates. The
 * Lens API token qualifies. This gets Stack working today without a separate
 * Entra app registration.
 *
 * TO DO before Kevin/Danille onboarding:
 *   1. Create a dedicated Stack app registration in Entra
 *   2. Add AUTH_CONFIG.stackApiScope = "api://<stack-client-id>/access_as_user"
 *   3. Switch this function to acquireTokenSilent({ scopes: [stackApiScope] })
 *   4. Add audience validation in stack-api/src/shared/auth.ts
 *
 * Only this helper needs updating when that happens — every call site in
 * src/lib/stack/api.ts goes through here.
 */
export async function acquireStackToken(): Promise<string> {
  return acquireLensApiToken();
}
